import {
  utcToZonedTime,
  format as formatTz,
  formatInTimeZone,
} from 'date-fns-tz';
import i18next from 'i18next';
import { format, isValid, parseISO } from 'date-fns';
import { mapUserLocaleToShorthand } from 'i18n/mapUserLocaleToShorthand';

interface CalendarEventParams {
  beginsAt: Date;
  endsAt: Date;
  subject: string;
  details: string;
}

export const formatDate = (
  inputDate: string | Date,
  formattingString?: string,
  timeZone = 'UTC',
): string | null => {
  const date = typeof inputDate === 'string' ? parseISO(inputDate) : inputDate;

  if (!isValid(date)) return null;

  let localeDefaultFormattingString;
  switch (i18next.language) {
    case 'en-GB':
      localeDefaultFormattingString = 'h:mm aaa, PPPP';
      break;

    default:
      localeDefaultFormattingString = 'PPPp';
  }

  return formatInTimeZone(
    date,
    timeZone,
    formattingString || localeDefaultFormattingString,
    {
      locale: mapUserLocaleToShorthand(),
    },
  );
};

export const convertToZonedTime = (timeZone: string): string => {
  const date = new Date();
  const tz = utcToZonedTime(date, timeZone);
  const locale = mapUserLocaleToShorthand();

  return `${timeZone.split('/').reverse().join(', ')} ${formatTz(tz, '(zzz)', {
    timeZone,
    locale,
  })}`;
};

const formatDateTime = (date: Date, timeFormat: string) =>
  format(utcToZonedTime(date, ''), timeFormat);

export const getGoogleCalendarLink = (params: CalendarEventParams) => {
  const timeFormat = "yyyyLLdd'T'HHmm00'Z'";
  const beginsAt = formatDateTime(params.beginsAt, timeFormat);
  const endsAt = formatDateTime(params.endsAt, timeFormat);

  const eventDetails = {
    action: 'TEMPLATE',
    text: params.subject,
    details: params.details,
    trp: 'true',
    dates: `${beginsAt}/${endsAt}`,
  };
  const searchParams = new URLSearchParams(eventDetails);
  const baseURL = 'https://calendar.google.com/calendar/render';

  return `${baseURL}?${searchParams.toString()}`;
};

const getMsCalendarLink = (baseUrl: string, params: CalendarEventParams) => {
  const timeFormat = "yyyy-LL-dd'T'HH:mm:00'Z'";
  const beginsAt = formatDateTime(params.beginsAt, timeFormat);
  const endsAt = formatDateTime(params.endsAt, timeFormat);

  const eventDetails = {
    path: '/calendar/action/compose',
    rru: 'addevent',
    startdt: beginsAt,
    enddt: endsAt,
    subject: params.subject,
    body: params.details,
  };
  const searchParams = new URLSearchParams(eventDetails);

  return `${baseUrl}?${searchParams.toString()}`;
};

export const getOutlookCalendarLink = (params: CalendarEventParams) =>
  getMsCalendarLink(
    'https://outlook.live.com/calendar/0/action/compose',
    params,
  );

export const getOffice365CalendarLink = (params: CalendarEventParams) =>
  getMsCalendarLink(
    'https://outlook.office.com/calendar/0/action/compose',
    params,
  );
